import React, { Component } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Plus,
  Search,
} from "lucide-react";
import moment from "moment";
import { Link } from "react-router-dom";
import AddService from "../components/AddService.tsx";
import ServiceDetail from "../components/ServiceDetail.tsx";
import LeaveModal from "../components/LeaveModal.tsx";
import ServiceStatusLegend, {
  ServiceStatusInlineLegend,
} from "../components/ServiceStatusLegend.tsx";
import { Button } from "../components/ui/button.tsx";
import ServiceApiService from "../services/service.service";
import UserService from "../services/user.service";
import TechnicianService from "../services/technician.service";
import {
  preserveCalendarItemId,
  restoreCalendarItemId,
} from "../lib/calendar-event.ts";
import type { CalendarDisplaySettings, ServiceItem, LeaveItem } from "./models";

type ServiceCalendarState = {
  servicesData: ServiceItem[];
  leavesData: LeaveItem[];
  showAddService: boolean;
  showServiceDetail: boolean;
  showLeaveModal: boolean;
  selectedLeave: LeaveItem | null;
  showLeaves: boolean;
  selectedSlotStart: Date | null;
  currentService: ServiceItem | null;
  date: Date;
  companyShortName: string;
} & CalendarDisplaySettings;

type ServiceCalendarStatus =
  | "scheduled"
  | "confirmed"
  | "paid"
  | "done"
  | "client_not_show_up"
  | "canceled_rob"
  | "canceled_client"
  | "canceled_overtime";

export default class ServiceCalendar extends Component<
  Record<string, never>,
  ServiceCalendarState
> {
  interval?: ReturnType<typeof setInterval>;
  calendarRef = React.createRef<FullCalendar>();

  constructor(props: Record<string, never>) {
    super(props);
    this.state = {
      servicesData: [] as ServiceItem[],
      leavesData: [] as LeaveItem[],
      showAddService: false,
      showServiceDetail: false,
      showLeaveModal: false,
      selectedLeave: null,
      showLeaves: localStorage.getItem("showLeaves") !== "false",
      selectedSlotStart: null,
      currentService: null as ServiceItem | null,
      date: new Date(),
      calendarStartHour: 8,
      calendarEndHour: 18,
      companyShortName: "RDS",
    };
  }

  handleShowLeavesChangedFromEvent = () => {
    this.setState(
      {
        showLeaves: localStorage.getItem("showLeaves") !== "false",
      },
      () => {
        this.getServicesAndLeaves();
      }
    );
  };

  handleLeavesUpdatedFromEvent = () => {
    this.getServicesAndLeaves();
  };

  componentDidMount() {
    this.loadCalendarSettings();
    this.getServicesAndLeaves();
    window.addEventListener(
      "showLeavesChanged",
      this.handleShowLeavesChangedFromEvent
    );
    window.addEventListener("leavesUpdated", this.handleLeavesUpdatedFromEvent);
    this.interval = setInterval(() => this.getServicesAndLeaves(), 15000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener(
      "showLeavesChanged",
      this.handleShowLeavesChangedFromEvent
    );
    window.removeEventListener(
      "leavesUpdated",
      this.handleLeavesUpdatedFromEvent
    );
  }

  loadCalendarSettings = async () => {
    try {
      const result = await UserService.getAccountSettings();
      this.setState({
        calendarStartHour: result.data.data?.calendarStartHour ?? 8,
        calendarEndHour: result.data.data?.calendarEndHour ?? 18,
        companyShortName: result.data.data?.companyShortName ?? "RDS",
      });
    } catch (error) {
      console.log(error);
    }
  };

  getServicesAndLeaves = async (props?: Date) => {
    try {
      const date = props || this.state.date;
      const firstDay = moment(
        new Date(date.getFullYear(), date.getMonth(), -10)
      )
        .toISOString()
        .replace("Z", "");
      const lastDay = moment(
        new Date(date.getFullYear(), date.getMonth() + 1, 10)
      )
        .toISOString()
        .replace("Z", "");

      const servicesPromise = ServiceApiService.getServices(firstDay, lastDay);
      const leavesPromise = this.state.showLeaves
        ? TechnicianService.getLeaves(firstDay, lastDay)
        : Promise.resolve({ data: { data: [] } });

      const [servicesResult, leavesResult] = await Promise.all([
        servicesPromise,
        leavesPromise,
      ]);

      const newServices = servicesResult.data.data || [];
      const newLeaves = (leavesResult.data.data || []).map((leave: any) => ({
        ...leave,
        isLeave: true,
      }));

      const { currentService } = this.state;
      let nextCurrentService = currentService;
      if (currentService) {
        const found = newServices.find((s: any) => s.id === currentService.id);
        if (found && JSON.stringify(found) !== JSON.stringify(currentService)) {
          nextCurrentService = found;
        }
      }

      if (
        JSON.stringify(this.state.servicesData) !==
          JSON.stringify(newServices) ||
        JSON.stringify(this.state.leavesData) !== JSON.stringify(newLeaves) ||
        nextCurrentService !== currentService
      ) {
        this.setState({
          servicesData: newServices,
          leavesData: newLeaves,
          currentService: nextCurrentService,
        });
      }
    } catch (error) {
      if (typeof error === "object" && error !== null && "response" in error) {
        const apiError = error as { response?: { data?: unknown } };
        console.log(apiError.response?.data);
      }
    }
  };

  handleSelectSlot = ({ start }: { start: Date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start < today) {
      return;
    }
    this.setState({
      showAddService: true,
      selectedSlotStart: this.normalizeStartDate(start),
    });
  };

  openAddService = (start?: Date | null) => {
    this.setState({
      showAddService: true,
      selectedSlotStart: this.normalizeStartDate(start),
    });
  };

  normalizeStartDate = (start?: Date | null) => {
    if (!start) {
      return null;
    }

    if (
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      start.getSeconds() === 0 &&
      start.getMilliseconds() === 0
    ) {
      const { calendarStartHour } = this.state;
      const normalizedStart = new Date(start);
      normalizedStart.setHours(calendarStartHour, 0, 0, 0);
      return normalizedStart;
    }

    return start;
  };

  handleSelectEvent = (event: any) => {
    const selectedItem = restoreCalendarItemId(event) as (
      | ServiceItem
      | LeaveItem
    ) & { isLeave?: boolean };

    if (selectedItem.isLeave) {
      this.setState({
        showLeaveModal: true,
        selectedLeave: selectedItem as LeaveItem,
      });
    } else {
      this.setState({
        showServiceDetail: true,
        currentService: selectedItem as ServiceItem,
      });
    }
  };

  onServiceChanged = (updatedService?: any) => {
    this.getServicesAndLeaves();
    if (updatedService) {
      this.setState({ currentService: updatedService });
    }
  };

  viewChanged = (date: Date) => {
    if (this.state.date.getTime() === date.getTime()) {
      return;
    }
    this.setState({ date });
    this.getServicesAndLeaves(date);
  };

  formatTitle = (event: any) => {
    if (event.isLeave) {
      return `Vacation: ${
        event.technicianName || event.technician_name || "Technician"
      }`;
    }
    const hour = moment(event.startDateTime).format("h a");
    if (event.client) {
      return `${hour}: ${event.client.clientName} (${event.durationHours}h)`;
    }
    return `${hour}: Service (${event.durationHours}h)`;
  };

  changeCalendarDate = (action: "prev" | "next" | "today") => {
    const calendar = this.calendarRef.current?.getApi();
    calendar?.[action]();
    if (calendar) {
      this.viewChanged(calendar.getDate());
    }
  };

  changeCalendarView = (
    viewName: "timeGridWeek" | "dayGridMonth" | "listWeek"
  ) => {
    this.calendarRef.current?.getApi().changeView(viewName);
  };

  eventClassNames = (event: any) => {
    if (event.isLeave) {
      return ["fc-event-leave"];
    }

    const status = event.status as ServiceCalendarStatus;
    if (!status) {
      return ["fc-event-status-scheduled"];
    }
    if (
      ["canceled_rob", "canceled_client", "canceled_overtime"].includes(status)
    ) {
      return ["fc-event-status-canceled"];
    }
    return [`fc-event-status-${status.replaceAll("_", "-")}`];
  };

  render() {
    const {
      servicesData,
      leavesData,
      showAddService,
      showServiceDetail,
      showLeaveModal,
      selectedLeave,
      showLeaves,
      selectedSlotStart,
      currentService,
      date,
      calendarStartHour,
      calendarEndHour,
      companyShortName,
    } = this.state;

    const combinedEvents = showLeaves
      ? [...servicesData, ...leavesData]
      : servicesData;
    const calendarEvents = combinedEvents.map((event: any) => {
      const isAllDay = event.is_all_day === true || event.isAllDay === true;
      const rawEnd = event.endDateTime || event.end_date_time;
      const end =
        isAllDay && rawEnd
          ? moment(rawEnd).add(1, "day").format("YYYY-MM-DD")
          : rawEnd;

      return {
        ...preserveCalendarItemId(event),
        start: event.startDateTime || event.start_date_time,
        end,
        allDay: isAllDay,
        title: this.formatTitle(event),
      };
    });
    const dateTitle = new Intl.DateTimeFormat("en", {
      month: "long",
      year: "numeric",
    }).format(date);
    const timeString = (hour: number) =>
      `${String(hour).padStart(2, "0")}:00:00`;

    return (
      <>
        <main className="service-calendar mx-auto w-full max-w-7xl px-3 pt-1 pb-5 sm:px-6">
          <header className="mb-3 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-sky-700">
                Service schedule
              </p>
              <h1 className="m-0 text-2xl font-semibold tracking-normal text-slate-950">
                {dateTitle}
              </h1>
              <ServiceStatusInlineLegend className="mt-2 hidden lg:flex" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center rounded-md border border-slate-300 bg-white p-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarDate("prev")}
                  aria-label="Previous period"
                  title="Previous period"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarDate("today")}
                  aria-label="Today"
                  title="Today"
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarDate("next")}
                  aria-label="Next period"
                  title="Next period"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center rounded-md border border-slate-300 bg-white p-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarView("timeGridWeek")}
                  aria-label="Week view"
                  title="Week view"
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarView("dayGridMonth")}
                  aria-label="Month view"
                  title="Month view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarView("listWeek")}
                  aria-label="List view"
                  title="List view"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <ServiceStatusLegend />
              <Link
                to="/service/list"
                aria-label="Search services"
                title="Search services"
              >
                <Button variant="outline" size="icon">
                  <Search className="h-4 w-4" />
                </Button>
              </Link>
              <Button onClick={() => this.openAddService(new Date())}>
                <Plus className="h-4 w-4" />
                New service
              </Button>
            </div>
          </header>
          <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <FullCalendar
              ref={this.calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
              ]}
              initialView="dayGridMonth"
              headerToolbar={false}
              height="calc(100vh - 210px)"
              expandRows
              selectable
              selectMirror
              selectAllow={(selectInfo) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return selectInfo.start >= today;
              }}
              longPressDelay={10}
              nowIndicator
              allDaySlot={showLeaves}
              slotMinTime={timeString(calendarStartHour)}
              slotMaxTime={timeString(calendarEndHour)}
              scrollTime={timeString(calendarStartHour)}
              events={calendarEvents}
              eventClassNames={(info) =>
                this.eventClassNames(info.event.extendedProps)
              }
              select={(info) => this.handleSelectSlot({ start: info.start })}
              eventClick={(info) => this.handleSelectEvent(info.event)}
              datesSet={(info) => this.viewChanged(info.view.currentStart)}
            />
          </section>
        </main>
        <AddService
          show={showAddService}
          onHide={() => this.setState({ showAddService: false })}
          defaultStartDate={selectedSlotStart}
          onServiceCreated={this.onServiceChanged}
        />
        <ServiceDetail
          show={showServiceDetail}
          onHide={() => this.setState({ showServiceDetail: false })}
          service={currentService}
          companyShortName={companyShortName}
          onServiceChange={this.onServiceChanged}
        />
        <LeaveModal
          show={showLeaveModal}
          onHide={() =>
            this.setState({ showLeaveModal: false, selectedLeave: null })
          }
          leave={selectedLeave}
          defaultStartDate={selectedSlotStart}
          onLeaveChanged={this.onServiceChanged}
          filterType="service"
        />
      </>
    );
  }
}
